// ============================================================================
// GsFacetFilter — filtre facette multi-selection (CHORE-FBK-FILTERS-MULTI)
// ============================================================================
// Norme industrie (Linear/GitHub/Jira) : facet filter en popover multi-selection
// avec cases a cocher et compteur dans le libelle (« Type · 2 »). Remplace les
// <select> natifs (menu OS hors design system + mono-valeur). Cree au moment de
// la PREMIERE implementation reelle (liste des tickets feedback) — regle
// « composant partage a la premiere implementation » (pattern GsActionBanner,
// UI_STANDARDS §4c).
//
// Semantique : la selection est un OU a l'interieur de la facette ; la
// combinaison ENTRE facettes (ET) appartient a l'appelant. Selection vide =
// « tous » (libelle neutre `allLabel`).
//
// A11y : bouton declencheur aria-expanded/aria-haspopup, popover role=listbox
// aria-multiselectable, options role=option aria-selected (cases a cocher
// visuelles — Tab/Espace/Entree natifs des boutons), Echap + clic-dehors
// ferment (pattern GsNotificationBell/GsUserMenu). Libelles par props (jamais
// de t() dans @gs/ui). AUCUNE couleur en dur — tokens slate/brand existants.

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';

export interface GsFacetFilterOption {
  value: string;
  label: string;
}

export interface GsFacetFilterProps {
  /** Libelle court de la facette — devient « {facetLabel} · n » quand n > 0. */
  facetLabel: string;
  /** Libelle neutre quand AUCUNE selection (ex. « Tous les types »). */
  allLabel: string;
  options: GsFacetFilterOption[];
  /** Valeurs selectionnees (vide = tous). */
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Libelle du bouton « Effacer » (i18n par props — defaut FR). */
  clearLabel?: string;
}

export function GsFacetFilter({
  facetLabel,
  allLabel,
  options,
  selected,
  onChange,
  clearLabel = 'Effacer',
}: GsFacetFilterProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fermeture clic-dehors + Echap (pattern GsNotificationBell).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const count = selected.length;
  // Compteur dans le libelle (« Type · 2 ») — neutre quand rien.
  const label = count === 0 ? allLabel : `${facetLabel} · ${count}`;

  return (
    <div ref={containerRef} className="relative">
      {/* Declencheur : meme gabarit que les controles de rangee (recherche,
          « Non lus ») — rounded-lg / py-1.5 / text-meta. Actif (n > 0) :
          font-medium + bordure encre (slate — brand reserve au wayfinding). */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={[
          'touch-manipulation inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-meta transition-colors',
          count > 0
            ? 'border-slate-400 bg-white font-medium text-slate-800 dark:border-slate-500 dark:bg-slate-900 dark:text-slate-100'
            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
        ].join(' ')}
      >
        {/* Entonnoir = repere visuel facette (MASTER-DS-POLISH-PASS). aria-hidden +
            slate (jamais brand : etat actif = wayfinding par la bordure/poids, pas la couleur). */}
        <Filter size={13} aria-hidden className="shrink-0 text-slate-400 dark:text-slate-500" />
        <span className="tabular-nums">{label}</span>
        <ChevronDown size={12} aria-hidden className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label={facetLabel}
          className="absolute left-0 top-full z-30 mt-1 min-w-40 rounded-lg border border-slate-200 bg-white p-1 shadow-md dark:border-slate-700 dark:bg-slate-800"
        >
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => toggle(opt.value)}
                className="touch-manipulation flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-meta text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/60"
              >
                {/* Case a cocher visuelle (l'etat ARIA vit sur l'option). */}
                <span
                  aria-hidden
                  className={[
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                    checked
                      ? 'border-slate-800 bg-slate-800 text-white dark:border-slate-400 dark:bg-slate-500'
                      : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900',
                  ].join(' ')}
                >
                  {checked && <Check size={10} strokeWidth={3} />}
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
          {count > 0 && (
            <>
              <div className="mx-1 my-1 border-t border-slate-100 dark:border-slate-700" />
              <button
                type="button"
                onClick={() => onChange([])}
                className="touch-manipulation w-full rounded-md px-2 py-1.5 text-left text-meta font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-200"
              >
                {clearLabel}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
